import { useState, useEffect, useCallback } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { capitalize, get, isEmpty, maxBy, startCase } from "lodash";
import { Container, Row, Col, Breadcrumb, Button, Form } from "react-bootstrap";
import EventService from "service/eventService";
import {
    getActuatorCommand,
    getDeviceListByGatewayId,
} from "service/gatewayService";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import "assets/css/event.css";
import {
    ActionType,
    AUTO_MODE,
    MANUAL_MODE,
    MOTOR_CONTROLS,
    MOTOR_MODE,
} from "constant";
import { NEW_ACTION_MESSAGE, UPDATE_ACTION_MESSAGE } from "constant/messages";
import {
    isHttpSuccess,
    isFourWiredMotorControllerApp,
    getSensorFromDevices,
} from "utils/functions";
import { ResponseDto } from "types";
import { EventActionActuatorStatus } from "generated/models";
import { getDisplayedActuatorMode } from "pages/events/EventDetails";
import { handleAPIResp } from "components/events/ActionDetail/handleActionApiUtil";
import { useModal } from "components/modals/ModalTemplate";
import { isLoRaWanGateway_ByGatewayId } from "utils/gatewayFunctions";

const getDeviceInfo = async ({
    gateway_id,
    device_id,
    said,
}: {
    gateway_id: string;
    device_id: string;
    said: string;
}) => {
    const deviceList: any = await getDeviceListByGatewayId(gateway_id);

    return getSensorFromDevices(deviceList, {
        gateway_id,
        device_id,
        said,
    });
};

const TriggerActuator = () => {
    const location: any = useLocation();
    const history: any = useHistory();
    const params: any = useParams();
    const selectedActuatorDetails = get(
        location,
        "state.selectedActuatorDetails",
        {}
    );
    const OFF = "off";
    const ON = "on";
    const [checkedOption, setCheckedOption] = useState(OFF);
    const [actuatorDetails, setActuatorDetails]: any = useState({});
    const [actionDetails, setActionDetails]: any = useState({
        name: "",
        type: "",
        mode: "",
        auto: false,
        gatewayId: "",
        ldsuUUID: "",
        said: "",
        isFourWireMotor: false,
        bus: "",
        gatewayName: "",
    });
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [ctrlOption, setCtrlOption] = useState(MOTOR_CONTROLS.FORWARD);

    const { openModal, modal } = useModal();

    const backToEventPage = useCallback(() => {
        history.push(`/event-details/${params.eventId}`);
    }, [params.eventId, history]);

    const isFourWiredMotor = selectedActuatorDetails.obj
        ? isFourWiredMotorControllerApp(
              selectedActuatorDetails.obj,
              selectedActuatorDetails.appId
          )
        : actionDetails.isFourWireMotor;

    useEffect(() => {
        if (!params.actionId) {
            const fetchActuator = async () => {
                const response: any = await getActuatorCommand(
                    selectedActuatorDetails.gatewayId,
                    selectedActuatorDetails.ldsuUUID,
                    selectedActuatorDetails.said
                );

                if (isHttpSuccess(response.status)) {
                    const { data: actuator } = response;

                    setActuatorDetails(actuator);

                    const deviceInfo = await getDeviceInfo({
                        gateway_id: selectedActuatorDetails.gatewayId,
                        device_id: selectedActuatorDetails.ldsuUUID,
                        said: selectedActuatorDetails.said,
                    });

                    setActionDetails({
                        gatewayId: selectedActuatorDetails.gatewayId,
                        ldsuUUID: selectedActuatorDetails.ldsuUUID,
                        bus: deviceInfo?.bus,
                        gatewayName: deviceInfo?.gatewayName,
                    });

                    setIsPageLoading(false);
                } else {
                    setIsPageLoading(false);
                    openModal({
                        key: "action_actuator_error",
                        modalType: "err",
                        modalContent: response.data.description,
                        okAction: () => {
                            backToEventPage();
                        },
                    });
                }
            };
            fetchActuator();
        } else {
            const fetchCurrentSensor = async () => {
                const response: any = await EventService.readOneAction(
                    params.eventId,
                    params.actionId
                );
                if (isHttpSuccess(response.status)) {
                    const { device_id, gateway_id, said, status, polarity } =
                        response.data.resource;

                    const actuatorRes: any = await getActuatorCommand(
                        gateway_id,
                        device_id,
                        said
                    );

                    const {
                        auto = false,
                        mode = "LEVEL",
                        name = "",
                    } = get(actuatorRes, "data", {});

                    if (mode === MOTOR_MODE) {
                        const motorControlMap = {
                            off: MOTOR_CONTROLS.PAUSE,
                            on: polarity
                                ? MOTOR_CONTROLS.FORWARD
                                : MOTOR_CONTROLS.REVERSE,
                        };
                        setCtrlOption(
                            motorControlMap[
                                status as keyof typeof motorControlMap
                            ]
                        );
                    }

                    const sensorInfo = await getDeviceInfo({
                        gateway_id,
                        device_id,
                        said,
                    });

                    setIsPageLoading(false);

                    setActionDetails({
                        name,
                        auto,
                        said,
                        mode,
                        type: sensorInfo?.sensor_type,
                        gatewayId: gateway_id,
                        ldsuUUID: device_id,
                        isFourWireMotor: isFourWiredMotorControllerApp(
                            sensorInfo?.OBJ,
                            sensorInfo?.APPID
                        ),
                        bus: sensorInfo?.bus,
                        gatewayName: sensorInfo?.gatewayName,
                    });

                    if (status === "on") {
                        setCheckedOption(ON);
                    }
                } else {
                    setIsPageLoading(false);
                    openModal({
                        key: "action_actuator_error",
                        modalType: "err",
                        modalContent: response.data.description,
                        okAction: () => {
                            backToEventPage();
                        },
                    });
                }
            };
            fetchCurrentSensor();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        params.actionId,
        params.eventId,
        selectedActuatorDetails.gatewayId,
        selectedActuatorDetails.ldsuUUID,
        selectedActuatorDetails.said,
    ]);

    const closeAction = () => {
        history.push(`/event-details/${params.eventId}`);
    };

    const transformPayload = async () => {
        const { gatewayId, ldsuUUID, said } = params.actionId
            ? actionDetails
            : selectedActuatorDetails;
        const value: EventActionActuatorStatus =
            checkedOption === OFF ? "off" : "on";

        const payload: any = {
            type_: ActionType.ACTUATOR,
            resource: {
                said: said,
                device_id: ldsuUUID,
                gateway_id: gatewayId,
                status: value,
            },
        };

        if (isFourWiredMotor) {
            if (ctrlOption === MOTOR_CONTROLS.PAUSE) {
                payload.resource.status = "off";
            } else {
                if (ctrlOption === MOTOR_CONTROLS.FORWARD) {
                    payload.resource.polarity = true;
                } else if (ctrlOption === MOTOR_CONTROLS.REVERSE) {
                    payload.resource.polarity = false;
                }
                payload.resource.status = "on";
            }
        }

        if (!params.actionId) {
            const { data } = await EventService.readOneEvent(params.eventId);
            const { actions = [] } = data;
            const latestAction: any = maxBy(actions, "order");
            const max = latestAction?.order ?? 0;

            return {
                ...payload,
                order: max + 1,
            };
        }

        return payload;
    };

    const handleButtonAction = async () => {
        let response: ResponseDto<any>;

        const payload = await transformPayload();

        if (!params.actionId) {
            response = await EventService.createAction(params.eventId, payload);
        } else {
            response = await EventService.updateAction(
                params.eventId,
                params.actionId,
                payload
            );
        }

        const successMessage = params.actionId
            ? UPDATE_ACTION_MESSAGE
            : NEW_ACTION_MESSAGE;

        handleAPIResp({
            response,
            successMessage,
            defaultErrorMessage: "Unable to save the Trigger Actuator action",
            backToEventPage: closeAction,
        });
    };

    const renderActuatorInfo = () => {
        if (selectedActuatorDetails || !isEmpty(actionDetails)) {
            let name: any;
            let type: any;
            let configMode: any;
            let actionMode: any;

            name =
                selectedActuatorDetails?.name ||
                actionDetails.name ||
                actuatorDetails.name;
            type = selectedActuatorDetails?.type || actionDetails.type;
            configMode = actuatorDetails?.mode || actionDetails.mode;
            actionMode =
                (!isEmpty(actuatorDetails) &&
                    (actuatorDetails?.auto ? AUTO_MODE : MANUAL_MODE)) ||
                (!isEmpty(actionDetails) &&
                    (actionDetails.auto ? AUTO_MODE : MANUAL_MODE));

            return (
                <div className="form-box mb-3">
                    <h5 className="mb-4">Actuator</h5>
                    <div className="action-device-details">
                        <h5>{name}</h5>
                        <p>{startCase(type)}</p>
                        <p className="text-primary-blue-4">
                            {`${actionDetails.gatewayName} - ${
                                isLoRaWanGateway_ByGatewayId(
                                    actionDetails.gatewayId
                                )
                                    ? "LoRaWAN"
                                    : `LDSBUS ${actionDetails.bus}`
                            }`}
                        </p>
                        <p className="text-primary-blue-4 mb-4">
                            UUID: {actionDetails.ldsuUUID}
                        </p>

                        {!isFourWiredMotor &&
                            getDisplayedActuatorMode(configMode) && (
                                <h6>
                                    Mode:{" "}
                                    <span className="text-primary-blue-4 ml-2">
                                        {getDisplayedActuatorMode(configMode)}
                                    </span>
                                </h6>
                            )}
                        <h6>
                            Control:{" "}
                            <span className="text-primary-blue-4 ml-2">
                                {capitalize(actionMode)}
                            </span>
                        </h6>
                    </div>
                </div>
            );
        }
    };

    return (
        <ContentWrapper isLoading={isPageLoading}>
            <Container>
                <Row>
                    <Col sm="12" className="event-detail-head">
                        <h5 className="page-title overflow-text">
                            Trigger Actuator
                        </h5>
                    </Col>
                </Row>
                <Row>
                    <Col sm="12">
                        <Breadcrumb className="w-100">
                            <Breadcrumb.Item href="/events">
                                Events
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                href={`/event-details/${params.eventId}`}
                            >
                                Event Details
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Action</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>
                <Row className="condition-detail-box">
                    <Col>
                        {renderActuatorInfo()}
                        <div className="form-box mb-3">
                            <h5 className="mb-4">Action</h5>
                            {isFourWiredMotor ? (
                                <Form.Row>
                                    {Object.values(MOTOR_CONTROLS).map(
                                        (item) => (
                                            <Col sm={2} key={item}>
                                                <Form.Check
                                                    name="mode"
                                                    id={item}
                                                    custom
                                                    type="radio"
                                                    onChange={() => {
                                                        setCtrlOption(item);
                                                    }}
                                                    checked={
                                                        ctrlOption === item
                                                    }
                                                    label={
                                                        <span className="ml-2">
                                                            {item.capitalize()}
                                                        </span>
                                                    }
                                                />
                                            </Col>
                                        )
                                    )}
                                </Form.Row>
                            ) : (
                                <Form.Row>
                                    <Col sm={2}>
                                        <Form.Check
                                            name="mode"
                                            id={ON}
                                            custom
                                            type="radio"
                                            onChange={() => {
                                                setCheckedOption(ON);
                                            }}
                                            checked={checkedOption === ON}
                                            label={
                                                <span className="ml-2">
                                                    Activate
                                                </span>
                                            }
                                        />
                                    </Col>
                                    <Col sm={2}>
                                        <Form.Check
                                            name="mode"
                                            id={OFF}
                                            custom
                                            type="radio"
                                            onChange={() => {
                                                setCheckedOption(OFF);
                                            }}
                                            checked={checkedOption === OFF}
                                            label={
                                                <span className="ml-2">
                                                    Deactivate
                                                </span>
                                            }
                                        />
                                    </Col>
                                </Form.Row>
                            )}
                        </div>
                        <Button
                            variant="secondary"
                            className="mr-2"
                            onClick={closeAction}
                        >
                            CANCEL
                        </Button>
                        <Button
                            variant="primary"
                            className="pl-4 pr-4"
                            onClick={handleButtonAction}
                        >
                            {!params.actionId ? "ADD" : "UPDATE"}
                        </Button>
                    </Col>
                </Row>
            </Container>

            {modal}
        </ContentWrapper>
    );
};

export default TriggerActuator;
